import {
    BadgeStyled,
    Container,
    Content,
    ContentLabel,
    ContentTab,
    ContentTabs,
    ContentTabsAndNotifications,
    InternalChatTabWrapper,
    LockBadge,
    Tab,
} from "./styled";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {observer} from "mobx-react";
import React, {useEffect, useState} from "react";
import Tabs, {Items} from "@dropDesk/presentation/components/tabs/background_style";
import {
    ActiveRouteEnum,
    ChatController,
} from "@dropDesk/presentation/pages/chat/controller/chat.controller";
import {InternalChatController} from "@dropDesk/presentation/pages/chat/controller/internal_chat.controller";
import {RoutesEnum} from "@dropDesk/domain/entities/routes/routes_enum";
import {ChatLists} from "@dropDesk/presentation/pages/chat/ui/chat_list";
import {InternalChatLists} from "src/presentation/pages/chat/ui/internal_chat/list";
import {TicketEntity} from "@dropDesk/domain/entities/ticket/ticket.entity";
import {InternalChatEntity} from "@dropDesk/domain/entities/internal_chat/internal_chat_entity";
import ContentChat from "@dropDesk/presentation/pages/chat/ui/content_chat";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {ColorScheme} from "@dropDesk/domain/entities/theme/color_scheme";
import {ContactsList} from "@dropDesk/presentation/pages/chat/ui/contact_list";
import {TabsChat} from "@dropDesk/domain/entities/chat/chat_enum";
import {ImageEditorController} from "@dropDesk/presentation/pages/image_editor/controller/image_editor.controller";
import {useLocation, useNavigate} from "react-router-dom";
import {TicketController} from "@dropDesk/presentation/pages/ticket/controller/ticket.controller";
import {SharedColors} from "@dropDesk/domain/entities/theme/app_colors";
import SoundConfiguration from "@dropDesk/presentation/pages/chat/ui/sound_configuration";
import ModalReconnectSocket from "@dropDesk/presentation/components/modals/modal_reconnect_socket";
import toast_message from "@dropDesk/utils/toast_message/toast_message";
import ContentInfoDisconnectedSubscription from "src/presentation/components/content_info_disconnected_subscription";
import {CancelTokenManager} from "@dropDesk/data/clients/http.client";
import {useSocketConnection} from "@dropDesk/presentation/hooks/useSocketConnection";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import DropDeskIcon from "@dropDesk/storage/icons";
import UnreadTileNotification from "@dropDesk/presentation/pages/chat/ui/unread_tile_notification";
import ModalsChat from "@dropDesk/presentation/pages/chat/ui/modals";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {t} from "i18next";
import {useBetween} from "use-between";
import {useModalChat} from "@dropDesk/presentation/pages/chat/ui/modals/visible_modal_search";
import {
    useModalChat as useModalContactList
} from "@dropDesk/presentation/pages/chat/ui/contact_list/modals/handle_change_visible";
import {hexToRGBA} from "@dropDesk/utils/helpers/colors";

const Chat = (observer(() => {

    const appController: AppController = useInjection(AppController);
    const colors: ColorScheme = appController.theme.colorScheme;
    const userLogged: UserEntity = appController.user!;
    const controller: ChatController = useInjection(ChatController);
    const internalChatController: InternalChatController = useInjection(InternalChatController);
    const ticketController: TicketController = useInjection(TicketController);
    const imageEditorController: ImageEditorController = useInjection(ImageEditorController);
    const location = useLocation();
    const navigate = useNavigate();
    const [visibleModalConnect, setVisibleModalConnect] = useState<boolean>(false);
    const [hasErrorConnectSocket, setHasErrorConnectSocket] = useState<boolean>(false);
    const [hasErrorConnectSocketTemporary, setHasErrorConnectSocketTemporary] = useState<boolean>(false);
    const [loadingReconnect, setLoadingReconnect] = useState<boolean>(false);
    const [contactLoadingId, setContactLoadingId] = useState<string | null>(null);
    const maxWidthContainer = 395;
    const defaultHeightTabBar = 50;
    const hasInternalChat = userLogged.company?.hasInternalChat ?? false;
    
    const {
        setVisibleModalNewInternalChat,
        setVisibleModalSearchUsers,
        setVisibleModalInternalChatUpsell,
        visibleModalDiscardImageEditor,
        setVisibleModalDiscardImageEditor,
    } = useBetween(useModalChat);

    const {setVisibleModalContactWithoutTicket} = useBetween(useModalContactList);

    const onClickContact = async (contact: UserEntity): Promise<void> => {
        if (contactLoadingId) {
            return;
        }
        if (controller.currentChat?.userClient?.id === contact.id) {
            return;
        }
        setContactLoadingId(contact.id);
        controller.eraseChat();
        const ticket = await ticketController.getLastTicketByContact(contact.id);
        setContactLoadingId(null);
        if (!ticket) {
            controller.setContactTicketId(null);
            setVisibleModalContactWithoutTicket({visible: true, contact});
            return;
        }
        controller.setContactTicketId(ticket.id);
        controller.setChat(ticket);
    }

    const handleGetLastUpdates = async (): Promise<void> => {
        await Promise.all([
            controller.listAllChats(controller.searchParam),
            controller.loadStaticMessages(),
        ]);
        fetchTicket();
        setHasErrorConnectSocketTemporary(false);
    }

    const onSuccess = (key: string) => {
        toastMessage.success(t(key));
        setVisibleModalNewInternalChat(false);
    }

    const handleDisconnect = (temporary: boolean) => {
        if (temporary) {
            setHasErrorConnectSocketTemporary(true);
        } else {
            setHasErrorConnectSocket(true);
            setVisibleModalConnect(true);
            setHasErrorConnectSocketTemporary(false);
        }
    }

    const {reconnect} = useSocketConnection({
        user: userLogged,
        origin: 'chat',
        handleDisconnect,
        handleGetLastUpdates,
        onConnected: () => {
            controller.observerChats();
            if (hasInternalChat) {
                internalChatController.observerInternalChats();
            }
        },
        enabled: true
    });

    const handleReconnectSocket = async (): Promise<void> => {
        try {
            setLoadingReconnect(true);
            await reconnect();
            await initialize();
            await Promise.all([
                controller.loadStaticMessages(),
            ]);
            fetchTicket();
            setVisibleModalConnect(false);
            setHasErrorConnectSocket(false);
            setLoadingReconnect(false);
            setHasErrorConnectSocketTemporary(false);
        } catch (e) {
            setHasErrorConnectSocket(true);
            setLoadingReconnect(false);
            toast_message.error('Não foi possível estabelecer a conexão. Por favor, tente novamente.' +
                ' Se o problema persistir, entre em contato com a equipe da DropDesk');
        }
    }

    useEffect(() => {
        initialize().then();
        return () => {
            CancelTokenManager.cancelToken();
            controller.stopNotificationWaiting();
            controller.removeAllValues();
            controller.dispose();
            internalChatController.removeAllValues();
            internalChatController.dispose();
            imageEditorController.deleteAllCache();
        }
    }, []);

    useEffect(() => {
        controller.playAudioWaitingListDepending();
    }, [controller.tableListWaiting, controller.soundQueueWaitingChat]);

    useEffect(() => {
        const state = location.state as { chatId?: string; internalChatId?: string } | null;
        if (state?.chatId && state.chatId !== controller.currentChat?.id && chatInitialized()) {
            handleClickPushNotification(state.chatId);
        } else if (state?.internalChatId && state.internalChatId !== internalChatController.currentInternalChat?.id && internalChatInitialize()) {
            handleClickPushNotificationInternalChat(state.internalChatId);
        }
    }, [location.state]);


    const initialize = async () => {
        try {
            const cancelTokenSource = CancelTokenManager.createNewToken();
            const calls: Promise<any>[] = [
                controller.initialize(
                    userLogged, ActiveRouteEnum.chat,
                    undefined, undefined, cancelTokenSource
                ),
            ];
            if (hasInternalChat) {
                calls.push(internalChatController.initialize(userLogged, cancelTokenSource));
            }
            await Promise.all(calls);
        } catch (error) {
            console.error(error);
        } finally {
            CancelTokenManager.clearToken();
        }
    }


    const handleClickPushNotification = (id: string) => {
        let ticket = controller.tableListWaiting.data.find((ticket) => ticket.id === id) || controller.tableListChats.data.find((ticket) => ticket.id === id);
        if (ticket) {
            ticketController.setTicket(ticket);
            controller.setActiveTabChat(ticket.hasAttendingStatus ? TabsChat.attending : TabsChat.waiting);
            onClickTicket(ticket);
        }
    }

    const handleClickPushNotificationInternalChat = (id: string) => {
        controller.setActiveTabChat(TabsChat.internalChat);
        let internalChat = internalChatController.tableListInternalChats.data.find((ic) => ic.id === id);
        if (internalChat) {
            onClickInternalChat(internalChat);
        }
    }

    const onSearch = async (searchParam: string, isChatAttending = true) => {
        if (isChatAttending) {
            await controller.listChatsAttending(searchParam ?? controller.searchParam);
        } else {
            await controller.listChatsWaiting(searchParam ?? controller.searchParam);
        }
    }

    function onClickTicket(ticket: TicketEntity) {
        controller.setContactTicketId(null);
        if (ticket.id !== controller.currentChat?.id) {
            controller.setChat(ticket);
        }
        internalChatController.eraseInternalChat();
    }

    function onClickInternalChat(internalChat: InternalChatEntity) {
        if (internalChat.id !== internalChatController.currentInternalChat?.id) {
            internalChatController.setCurrentInternalChat(internalChat);
        }
        controller.eraseChat();
    }

    const onSearchInternalChat = async (searchParam: string) => {
        await internalChatController.listInternalChats(searchParam);
    }

    const chatInitialized = (): boolean => {
        return controller.tableListChats.totalRows > 0 || controller.tableListWaiting.totalRows > 0;
    }

    const internalChatInitialize = (): boolean => {
        return internalChatController.tableListInternalChats.totalRows > 0;
    }
    const onClickNewInternalChat = () => {
        internalChatController.makeInternalChat();
        setVisibleModalNewInternalChat(true);
    }

    const handleClickAddInternalChatUser = () => {
        internalChatController.setInternalChatUsersDefault(internalChatController.internalChat?.users ?? []);
        setVisibleModalSearchUsers(true);
    }
    const items: Array<Items> = [
        {
            label:

                <Tab height={defaultHeightTabBar}>
                    <SoundConfiguration
                        text={`Notificação sonora ao receber mensagem`}
                        useSound={controller.soundNewMessageChat}
                        setSound={(value) => controller.setSoundNewMessageChat(value)}
                    />
                    <ContentTab>
                        <BadgeStyled
                            colorcount={SharedColors.white}
                            bordercolor={colors.accent}
                            color={colors.accent}
                            style={{}}
                            count={controller.tableListChats.totalRows}
                            overflowCount={99}
                            textfontsizesm={15}
                            size="small"
                            offset={[0.7, 1.3]}
                        >
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.userChecked}
                                style={{display: 'flex', marginBottom: 1}}
                                size={18}
                                color={colors.text}
                            />
                        </BadgeStyled>
                        <ContentLabel>
                            Atendendo
                        </ContentLabel>
                    </ContentTab>
                    {controller.countNotificationAttending > 0 && <UnreadTileNotification/>}
                </Tab>,
            key: TabsChat.attending,
            children: <ChatLists
                onClick={(ticket) => onClickTicket(ticket)}
                searchParam={controller.searchParam}
                tickets={controller.filteredListChat}
                loadMore={() => controller.getDataFromPageChatsAttending()}
                loadingMore={controller.loading && !controller.loadingMessage}
                loading={controller.loading}
                hasMore={controller.tableListChats.hasMore}
                useSearch={true}
                onSearch={onSearch}
                idTicketSelected={controller.currentChat?.id}
                userLogged={userLogged}
                disabled={imageEditorController.loading}
            />,
        },
        {
            label:
                <Tab height={defaultHeightTabBar}>
                    <SoundConfiguration
                        text={`Notificação sonora na fila de espera`}
                        useSound={controller.soundQueueWaitingChat}
                        setSound={(value) => controller.setSoundQueueWaitChat(value)}
                    />
                    <ContentTab>
                        <BadgeStyled
                            colorcount={SharedColors.white}
                            bordercolor={colors.accent}
                            color={colors.accent}
                            style={{}}
                            count={controller.tableListWaiting.totalRows}
                            overflowCount={99}
                            textfontsizesm={15}
                            size="small"
                            offset={[-3, 2]}
                        >
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.userClock}
                                style={{display: 'flex'}}
                                size={20}
                                color={colors.text}
                            />
                        </BadgeStyled>
                        <ContentLabel>

                            Espera
                        </ContentLabel>
                    </ContentTab>
                </Tab>,
            key: TabsChat.waiting,
            children: <ChatLists
                onClick={(ticket) => onClickTicket(ticket)}
                searchParam={controller.searchParam}
                tickets={controller.filteredListWaiting}
                loadMore={() => controller.getDataFromPageChatsWaiting()}
                loadingMore={controller.loading && !controller.loadingMessage}
                loading={controller.loading}
                hasMore={controller.tableListWaiting.hasMore}
                useSearch={true}
                onSearch={(searchParam) => onSearch(searchParam, false)}
                idTicketSelected={controller.currentChat?.id}
                userLogged={userLogged}
                disabled={imageEditorController.loading}
            />,
        },
        {
            label: <Tab height={defaultHeightTabBar}>
                <ContentTab>
                    <DropDeskIcon
                        icon={ICONS_DROPDESK.users}
                        style={{display: 'flex'}}
                        size={20}
                        color={colors.text}
                    />
                    Contatos
                </ContentTab>
            </Tab>,
            key: TabsChat.contacts,
            children: <ContactsList
                handleInitChat={(ticket) => controller.initChatDependantFromContact(ticket)}
                maxWidthContainer={maxWidthContainer}
                onClickContact={(contact) => onClickContact(contact)}
                contactLoadingId={contactLoadingId}
            />,
        },
        {
            label: <Tab height={defaultHeightTabBar}
            >
                <InternalChatTabWrapper
                    disabled={!hasInternalChat}
                    disabledbackground={hexToRGBA(colors.hint, 0.2)}
                >
                    {hasInternalChat && (
                        <SoundConfiguration
                            text={`Notificação sonora ao receber mensagem`}
                            useSound={internalChatController.soundNewMessageInternalChat}
                            setSound={(value) => internalChatController.setSoundNewMessageInternalChat(value)}
                        />
                    )}
                    {!hasInternalChat && (
                        <LockBadge color={colors.textError}>
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.padlock}
                                size={12}
                                color={colors.textError}
                            />
                        </LockBadge>
                    )}
                    <ContentTab>
                        <BadgeStyled
                            colorcount={SharedColors.white}
                            bordercolor={colors.accent}
                            color={colors.accent}
                            style={{}}
                            count={hasInternalChat ? internalChatController.tableListInternalChats.totalRows : 0}
                            overflowCount={99}
                            textfontsizesm={15}
                            size="small"
                            offset={[-3, 2]}
                        >
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.internalChat}
                                style={{display: 'flex'}}
                                size={20}
                                color={colors.text}
                            />
                        </BadgeStyled>
                        <ContentLabel>
                            Interno
                        </ContentLabel>
                    </ContentTab>
                    {hasInternalChat && internalChatController.countNotificationDocumentHead > 0 && <UnreadTileNotification/>}
                </InternalChatTabWrapper>
            </Tab>,
            key: TabsChat.internalChat,
            children: <InternalChatLists
                onClick={(internalChat) => onClickInternalChat(internalChat)}
                searchParam={internalChatController.searchParam}
                internalChats={internalChatController.filteredList}
                loadMore={() => internalChatController.getDataFromPageInternalChats()}
                loadingMore={internalChatController.loading && !internalChatController.loadingMessage}
                loading={internalChatController.loading}
                hasMore={internalChatController.tableListInternalChats.hasMore}
                useSearch={true}
                onSearch={onSearchInternalChat}
                idInternalChatSelected={internalChatController.currentInternalChat?.id}
                userLogged={userLogged}
                disabled={imageEditorController.loading}
                onClickNewInternalChat={onClickNewInternalChat}
            />,
        }];


    const fetchTicket = (): void => {
        if (controller.currentChat) {
            const ticket = controller.getTicketFromCache(controller.currentChat.id);
            if (ticket) {
                controller.setChat(ticket!, false);
            }
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            if (visibleModalDiscardImageEditor) {
                event.preventDefault();
                event.stopPropagation();
                setVisibleModalDiscardImageEditor(false);
                return;
            }
            if (imageEditorController.filesEdition.length > 0) {
                event.preventDefault();
                event.stopPropagation();
                setVisibleModalDiscardImageEditor(true);
                return;
            }
            controller.eraseChat();
            internalChatController.eraseInternalChat();
        }
    };

    const handleClickOnSave = async (): Promise<void> => {
        if (internalChatController.internalChat) {
            if (internalChatController.internalChat.isUnsaved()) {
                await internalChatController.set(onSuccess);
            } else {
                setVisibleModalNewInternalChat(false);
                await internalChatController.changeDescription(onSuccess)
            }
        }
    }

    const handleClickEditUsers = (internalChat: InternalChatEntity) => {
        internalChatController.setInternalChat(internalChat);
        internalChatController.setInternalChatUsersDefault(internalChat?.users ?? []);
        setVisibleModalSearchUsers(true);
    }

    return (
        <Container
            background={colors.onBackground}
            onKeyDown={handleKeyDown}
            tabIndex={0}>
            <Content>
                <ContentTabsAndNotifications>
                    <ContentTabs width={maxWidthContainer}>
                        {(hasErrorConnectSocket || hasErrorConnectSocketTemporary) &&
                            <ContentInfoDisconnectedSubscription
                                loading={loadingReconnect}
                                onClickReconnect={() => handleReconnectSocket()}
                                visibleTemporaryInformation={hasErrorConnectSocketTemporary}
                            />}
                        <Tabs
                            defaultActiveKey={controller.activeTabChat}
                            activeKey={controller.activeTabChat}
                            onChange={(key) => {
                                if (key === TabsChat.internalChat && !hasInternalChat) {
                                    setVisibleModalInternalChatUpsell(true);
                                    return;
                                }
                                controller.setActiveTabChat(key as TabsChat);
                            }}
                            items={userLogged.permissionScreenClients ? items : items.filter((entry) => entry.key !== 'contacts')}
                            flex={true}
                            height={defaultHeightTabBar}
                        />
                    </ContentTabs>

                    <ContentChat
                        chatId={controller.currentChat?.id}
                        widthContainerListChats={maxWidthContainer}
                        internalChatId={internalChatController.currentInternalChat?.id}
                    />

                    <ModalReconnectSocket
                        loading={controller.loading}
                        onRequestClose={() => setVisibleModalConnect(false)}
                        onRequestSave={() => handleReconnectSocket()}
                        visible={visibleModalConnect}
                    />

                    <ModalsChat
                        loadingInternalChat={internalChatController.loading && !!internalChatController.loadingMessage}
                        onUpdate={(internalChat) => internalChatController.setInternalChat(internalChat)}
                        internalChat={internalChatController.internalChat}
                        onClickSave={() => handleClickOnSave()}
                        internalChatUsersDefault={internalChatController.internalChatUsersDefault}
                        onChangeUsersLinked={(user) => internalChatController.onChangeUsersLinked(user)}
                        onDeleteUsersLinked={(id) => internalChatController.onDeleteUsersLinked(id)}
                        onClickAddUser={() => handleClickAddInternalChatUser()}
                        onDeleteInternalChat={() => internalChatController.delete(onSuccess)}

                        loadingListUsers={internalChatController.loadingListUsers}
                        hasMoreListUsers={internalChatController.tableInternalChatUsers.hasMore}
                        internalChatUsers={internalChatController.tableInternalChatUsers.data}
                        loadMoreListUsers={() => internalChatController.loadMoreUsersDataFromPage()}
                        searchParamUsers={internalChatController.searchParamUsers}
                        onSearchListUsers={(searchParam) => internalChatController.listInternalChatUsers(searchParam)}
                        onClickEditUsers={() => handleClickEditUsers(internalChatController.currentInternalChat!)}
                        disposeListUsers={() => internalChatController.disposeListUsers()}
                        onSaveEditUsers={!internalChatController.internalChat?.isUnsaved() ? internalChatController.changeUsers : undefined}
                        isAdmin={userLogged.permissionAdmin}
                        onActivateInternalChat={() => navigate(RoutesEnum.extensions)}
                        onDiscardImageEditor={() => imageEditorController.clearFiles()}
                    />

                </ContentTabsAndNotifications>

            </Content>
        </Container>
    );
}));

export default Chat;
